import { Injectable } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { Conversation } from '@elevenlabs/client';
import { checkIsWebView } from '../../../../../helpers/screen-utils';
import { HybridStorageKeys, HybridStorageService } from '../../../../services/hybrid-storage.service';

export interface WidgetPosition {
  bottom: number;
  right: number;
}

export interface WidgetState {
  showVoiceChat: boolean;
  showChat: boolean;
  showAssistantMenu: boolean;
  showVideos: boolean;
}

export interface ChatMessage {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

export interface ConversationState {
  conversation: Conversation | null;
  messages: ChatMessage[];
  isConnected: boolean;
}

@Injectable({
  providedIn: 'root'
})
export class AiChatWidgetService {
  private get DEFAULT_POSITION(): WidgetPosition {
    // Desktop: 20px from bottom
    // Mobile: 180px from bottom — keeps the widget above the FAB buttons (which sit at ~100–156px)
    return { bottom: checkIsWebView() ? 20 : 180, right: 20 };
  }

  // Widget state observable
  private widgetStateSubject = new BehaviorSubject<WidgetState>({
    showVoiceChat: false,
    showChat: false,
    showAssistantMenu: false,
    showVideos: false
  });

  public widgetState$ = this.widgetStateSubject.asObservable();

  // Emitted when both chats are fully reset (e.g. on impersonation start/stop) so the
  // live chat components can drop their in-memory references and re-sync from storage.
  private resetSubject = new Subject<void>();
  public reset$ = this.resetSubject.asObservable();

  // Widget position observable
  private widgetPositionSubject = new BehaviorSubject<WidgetPosition>(this.DEFAULT_POSITION);
  public widgetPosition$ = this.widgetPositionSubject.asObservable();

  // Conversation state storage
  private typedChatState: ConversationState = {
    conversation: null,
    messages: [],
    isConnected: false
  };

  private voiceChatState: ConversationState = {
    conversation: null,
    messages: [],
    isConnected: false
  };

  private initialized = false;

  constructor(private storage: HybridStorageService) {
    this.initializeFromStorage();
  }

  private async initializeFromStorage(): Promise<void> {
    if (this.initialized) return;

    // Load position
    const position = await this.storage.getItem<WidgetPosition>(HybridStorageKeys.AiChat_WidgetPosition);
    if (position) {
      this.widgetPositionSubject.next(this.constrainPosition(position));
    }

    // Load typed chat state
    const typedMessages = await this.storage.getItem<ChatMessage[]>(HybridStorageKeys.AiChat_TypedMessages);
    if (typedMessages) {
      this.typedChatState.messages = typedMessages.map(m => ({
        ...m,
        timestamp: new Date(m.timestamp)
      }));
    }

    // Load voice chat state
    const voiceMessages = await this.storage.getItem<ChatMessage[]>(HybridStorageKeys.AiChat_VoiceMessages);
    if (voiceMessages) {
      this.voiceChatState.messages = voiceMessages.map(m => ({
        ...m,
        timestamp: new Date(m.timestamp)
      }));
    }

    this.initialized = true;
  }

  // Get current state
  getState(): WidgetState {
    return this.widgetStateSubject.value;
  }

  // Update widget state
  updateState(state: Partial<WidgetState>): void {
    const currentState = this.widgetStateSubject.value;
    this.widgetStateSubject.next({ ...currentState, ...state });
  }

  // Close all modals and menus
  closeAll(): void {
    this.widgetStateSubject.next({
      showVoiceChat: false,
      showChat: false,
      showAssistantMenu: false,
      showVideos: false
    });
  }

  // Position management
  getPosition(): WidgetPosition {
    return this.widgetPositionSubject.value;
  }

  setPosition(position: WidgetPosition): void {
    this.widgetPositionSubject.next(position);
    this.savePosition(position);
  }

  // Update position without saving to storage (for dragging performance)
  updatePositionWithoutSave(position: WidgetPosition): void {
    this.widgetPositionSubject.next(position);
  }

  // Save the current position to storage
  saveCurrentPosition(): void {
    this.savePosition(this.widgetPositionSubject.value);
  }

  private constrainPosition(position: WidgetPosition): WidgetPosition {
    const ICON_SIZE = 80;

    // Allow icon anywhere on screen, just keep it within viewport bounds
    const maxRight = window.innerWidth - ICON_SIZE;
    const maxBottom = window.innerHeight - ICON_SIZE;

    return {
      right: Math.max(0, Math.min(maxRight, position.right)),
      bottom: Math.max(0, Math.min(maxBottom, position.bottom))
    };
  }

  private savePosition(position: WidgetPosition): void {
    this.storage.setItem(HybridStorageKeys.AiChat_WidgetPosition, position);
  }

  resetPosition(): void {
    this.setPosition(this.DEFAULT_POSITION);
  }

  // Typed Chat conversation management
  getTypedChatState(): ConversationState {
    return this.typedChatState;
  }

  setTypedChatConversation(conversation: Conversation | null, isConnected: boolean): void {
    this.typedChatState.conversation = conversation;
    this.typedChatState.isConnected = isConnected;
  }

  addTypedChatMessage(message: ChatMessage): void {
    this.typedChatState.messages.push(message);
    this.storage.setItem(HybridStorageKeys.AiChat_TypedMessages, this.typedChatState.messages);
  }

  getTypedChatMessages(): ChatMessage[] {
    return this.typedChatState.messages;
  }

  clearTypedChat(): void {
    this.typedChatState = {
      conversation: null,
      messages: [],
      isConnected: false
    };
    this.storage.removeItem(HybridStorageKeys.AiChat_TypedMessages);
  }

  // Voice Chat conversation management
  getVoiceChatState(): ConversationState {
    return this.voiceChatState;
  }

  setVoiceChatConversation(conversation: Conversation | null, isConnected: boolean): void {
    this.voiceChatState.conversation = conversation;
    this.voiceChatState.isConnected = isConnected;
  }

  addVoiceChatMessage(message: ChatMessage): void {
    this.voiceChatState.messages.push(message);
    this.storage.setItem(HybridStorageKeys.AiChat_VoiceMessages, this.voiceChatState.messages);
  }

  getVoiceChatMessages(): ChatMessage[] {
    return this.voiceChatState.messages;
  }

  clearVoiceChat(): void {
    this.voiceChatState = {
      conversation: null,
      messages: [],
      isConnected: false
    };
    this.storage.removeItem(HybridStorageKeys.AiChat_VoiceMessages);
  }

  // End session but preserve messages - user can still see history
  async disconnectTypedChat(): Promise<void> {
    if (this.typedChatState.conversation) {
      try {
        await this.typedChatState.conversation.endSession();
      } catch (error) {
        console.error('Error ending typed chat session:', error);
      }
    }
    // Only clear the conversation, not the messages
    this.typedChatState.conversation = null;
    this.typedChatState.isConnected = false;
  }

  async disconnectVoiceChat(): Promise<void> {
    if (this.voiceChatState.conversation) {
      try {
        await this.voiceChatState.conversation.endSession();
      } catch (error) {
        console.error('Error ending voice chat session:', error);
      }
    }
    // Only clear the conversation, not the messages
    this.voiceChatState.conversation = null;
    this.voiceChatState.isConnected = false;
  }

  /**
   * Fully reset both chats: end any live sessions and wipe messages + storage, then
   * notify the chat components (via reset$) to drop their in-memory state. Used on
   * impersonation start/stop so one user's conversation never bleeds into another's.
   */
  resetAll(): void {
    // End live sessions in the background; the clears below reset state immediately.
    void this.disconnectTypedChat();
    void this.disconnectVoiceChat();
    this.clearTypedChat();
    this.clearVoiceChat();
    this.resetSubject.next();
  }
}
